import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { adminCustomerService } from '@/services/api/adminCustomerService';
import type {
  Customer,
  CustomerFilters,
  PaginationMeta,
  CustomerUpdateData,
  CustomerStatusUpdateData,
  CustomerCategoryUpdateData
} from '@/types/customer';

export const useAdminCustomerStore = defineStore('adminCustomer', () => {
  // State
  const customers = ref<Customer[]>([]);
  const selectedCustomer = ref<Customer | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  
  const filters = ref<CustomerFilters>({
    search: '',
    status: '',
    brand: '',
    page: 1,
    per_page: 10
  });

  const pagination = ref<PaginationMeta>({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0
  });

  // Getters
  const pendingCustomers = computed(() =>
    customers.value.filter(c => c.status === 'pending')
  );

  const activeCustomers = computed(() =>
    customers.value.filter(c => c.status === 'active')
  );

  const rejectedCustomers = computed(() =>
    customers.value.filter(c => c.status === 'rejected')
  );

  // Actions
  async function fetchCustomers() {
    loading.value = true;
    error.value = null;

    try {
      const response = await adminCustomerService.getCustomers(filters.value);
      customers.value = response.data;
      pagination.value = response.meta;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch customers';
      console.error('Error fetching customers:', err);
    } finally {
      loading.value = false;
    }
  }

  async function fetchCustomer(userId: number) {
    loading.value = true;
    error.value = null;

    try {
      const response = await adminCustomerService.getCustomer(userId);
      selectedCustomer.value = response.data;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch customer';
      console.error('Error fetching customer:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function approveCustomer(userId: number) {
    loading.value = true;
    error.value = null;

    try {
      const data: CustomerStatusUpdateData = { status: 'active' };
      await adminCustomerService.updateCustomerStatus(userId, data);

      // Refresh the customer list
      await fetchCustomers();
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to approve customer';
      console.error('Error approving customer:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function rejectCustomer(userId: number) {
    loading.value = true;
    error.value = null;

    try {
      const data: CustomerStatusUpdateData = { status: 'rejected' };
      await adminCustomerService.updateCustomerStatus(userId, data);
      
      // Refresh the customer list
      await fetchCustomers();
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to reject customer';
      console.error('Error rejecting customer:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateCustomer(userId: number, data: CustomerUpdateData) {
    loading.value = true;
    error.value = null;

    try {
      await adminCustomerService.updateCustomer(userId, data);
      
      // Refresh the customer list
      await fetchCustomers();
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update customer';
      console.error('Error updating customer:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function deleteCustomer(userId: number) {
    loading.value = true;
    error.value = null;

    try {
      await adminCustomerService.deleteCustomer(userId);

      // Refresh the customer list
      await fetchCustomers();
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to delete customer';
      console.error('Error deleting customer:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function resetCustomerPassword(userId: number) {
    loading.value = true;
    error.value = null;

    try {
      await adminCustomerService.resetCustomerPassword(userId);
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to send password reset email';
      console.error('Error resetting customer password:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  function setFilters(newFilters: Partial<CustomerFilters>) {
    filters.value = { ...filters.value, ...newFilters };

    // Ensure per_page is always set to a valid value
    if (!filters.value.per_page || filters.value.per_page <= 0) {
      filters.value.per_page = 10;
    }

    // Ensure page is always set to a valid value
    if (!filters.value.page || filters.value.page <= 0) {
      filters.value.page = 1;
    }
  }

  function resetFilters() {
    filters.value = {
      search: '',
      status: '',
      brand: '',
      page: 1,
      per_page: 10
    };
  }

  function clearError() {
    error.value = null;
  }

  async function exportCustomers() {
    loading.value = true;
    error.value = null;

    try {
      // Import XLSX dynamically
      const XLSX = await import('xlsx');

      // Fetch ALL filtered customers (no pagination)
      const allCustomers = await adminCustomerService.getCustomersForExport(filters.value);

      if (allCustomers.length === 0) {
        throw new Error('No customers to export with current filters');
      }

      // Transform data for export
      const exportData = allCustomers.map(customer => {
        const detail = customer.userDetail || customer.user_detail;
        const addressParts = [
          detail?.house_no,
          detail?.street,
          detail?.barangay,
          detail?.city
        ].filter(Boolean);

        return {
          'Customer Name': `${customer.first_name || ''} ${customer.last_name || ''}`.trim(),
          'Email': customer.email || '',
          'Mobile': detail?.mobile || '',
          'Brand': customer.brand?.brand_name || '',
          'Address': addressParts.join(', '),
          'Status': customer.status || '',
          'Vehicles Count': customer.vehicles_count ?? 0,
          'Tires Count': customer.tires_count ?? 0,
          'Date Joined': customer.created_at ? new Date(customer.created_at).toLocaleDateString() : ''
        };
      });

      // Create worksheet and workbook
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Customers');

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `customers_export_${timestamp}.xlsx`;

      // Trigger download
      XLSX.writeFile(workbook, filename);

      return true;
    } catch (err: any) {
      error.value = err.message || 'Failed to export customers';
      console.error('Error exporting customers:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateCustomerCategory(userId: number, data: CustomerCategoryUpdateData) {
    loading.value = true;
    error.value = null;
    try {
      await adminCustomerService.updateCustomerCategory(userId, data);
      await fetchCustomers();
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update customer category';
      console.error('Error updating customer category:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    // State
    customers,
    selectedCustomer,
    loading,
    error,
    filters,
    pagination,
    // Getters
    pendingCustomers,
    activeCustomers,
    rejectedCustomers,
    // Actions
    fetchCustomers,
    fetchCustomer,
    approveCustomer,
    rejectCustomer,
    updateCustomer,
    deleteCustomer,
    resetCustomerPassword,
    setFilters,
    resetFilters,
    clearError,
    exportCustomers,
    updateCustomerCategory
  };
});

